import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import ArticleCard from '@/components/base/ArticleCard';
import { categories } from '@/mocks/categories';
import { filterArticles } from '@/lib/content';

interface InsightExplorerProps {
  initialCategory?: string;
  showCategoryFilter?: boolean;
}

export default function InsightExplorer({
  initialCategory = 'all',
  showCategoryFilter = true,
}: InsightExplorerProps) {
  const [searchParams] = useSearchParams();
  const [keyword, setKeyword] = useState(() => searchParams.get('q') ?? '');
  const [category, setCategory] = useState(initialCategory);
  const [sort, setSort] = useState('latest');

  const results = useMemo(
    () => filterArticles({ keyword, category, sort }),
    [keyword, category, sort],
  );

  const tabs = [{ slug: 'all', name: '全部' }, ...categories.map((c) => ({ slug: c.slug, name: c.name }))];

  return (
    <div>
      <div className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="relative w-full lg:max-w-md">
            <i className="ri-search-line pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-base text-foreground-400" />
            <input
              type="search"
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              placeholder="搜索标题、关键词或主题"
              aria-label="搜索观察内容"
              className="w-full rounded-md border border-background-300 bg-background-50 py-2.5 pl-10 pr-4 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
            />
          </div>

          <div className="flex items-center gap-3">
            <span className="hidden font-label text-xs text-foreground-500 sm:block">排序</span>
            <div className="flex rounded-full border border-background-300 p-1">
              {[
                { key: 'latest', name: '最新' },
                { key: 'hot', name: '最热' },
              ].map((item) => (
                <button
                  key={item.key}
                  type="button"
                  onClick={() => setSort(item.key)}
                  className={`cursor-pointer whitespace-nowrap rounded-full px-4 py-1.5 font-label text-sm transition-colors ${
                    sort === item.key
                      ? 'bg-foreground-950 text-background-50'
                      : 'text-foreground-600 hover:text-foreground-950'
                  }`}
                >
                  {item.name}
                </button>
              ))}
            </div>
          </div>
        </div>

        {showCategoryFilter && (
          <div className="mt-4 flex flex-wrap gap-2 border-t border-background-200 pt-4">
            {tabs.map((tab) => (
              <button
                key={tab.slug}
                type="button"
                onClick={() => setCategory(tab.slug)}
                className={`cursor-pointer whitespace-nowrap rounded-full border px-3.5 py-1.5 font-label text-sm transition-colors ${
                  category === tab.slug
                    ? 'border-primary-500 bg-primary-500 text-background-50'
                    : 'border-background-300 text-foreground-700 hover:border-primary-300 hover:text-primary-700'
                }`}
              >
                {tab.name}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="mt-5 flex items-center justify-between">
        <p className="font-label text-sm text-foreground-500">
          共 <span className="font-medium text-foreground-900">{results.length}</span> 条观察
          {keyword && <span> · 关键词「{keyword}」</span>}
        </p>
        {(keyword || category !== initialCategory) && (
          <button
            type="button"
            onClick={() => {
              setKeyword('');
              setCategory(initialCategory);
            }}
            className="cursor-pointer font-label text-sm text-primary-700 transition-colors hover:text-primary-500"
          >
            重置筛选
          </button>
        )}
      </div>

      {results.length > 0 ? (
        <div className="mt-6 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {results.map((article) => (
            <ArticleCard key={article.id} article={article} />
          ))}
        </div>
      ) : (
        <div className="mt-6 rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-16 text-center">
          <i className="ri-file-search-line text-3xl text-foreground-300" />
          <h3 className="mt-4 font-heading text-lg font-bold text-foreground-900">没有找到相关观察</h3>
          <p className="mx-auto mt-2 max-w-md text-sm leading-relaxed text-foreground-600">
            换一个关键词试试，或者直接浏览全部主题分类，看看是否有更接近你需求的内容。
          </p>
          <button
            type="button"
            onClick={() => {
              setKeyword('');
              setCategory('all');
            }}
            className="mt-5 cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 font-label text-sm text-background-50 transition-colors hover:bg-primary-600"
          >
            查看全部内容
          </button>
        </div>
      )}
    </div>
  );
}